import { TooltipDirective } from 'src/app/directives/tooltip.directive';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { faEdit, faCheck, faCancel, faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { Subscription } from 'rxjs';
import { EnumAcl } from '@simplyfly/shared';
import { IEnquiryFlight } from '@simplyfly/shared';
import {
  IFlightChecklistTemplate,
  IFlightChecklistTemplateField,
  IFlightChecklistTemplateFieldOption,
  EnumFlightChecklistTemplateFieldType,
  EnumFlightChecklistTemplateFieldOptionStatus
} from '@simplyfly/shared';
import { IFlightChecklist, IFlightChecklistFieldDataValue } from '@simplyfly/shared';
import {
  appendFlightChecklistFieldValue,
  initFlightChecklistFromTemplate
} from 'src/app/interfaces/flight-checklist.helpers';
import { IUser } from '@simplyfly/shared';
import { getUserFullname } from 'src/app/interfaces/user.helpers';
import { AclService } from 'src/app/services/acl.service';
import { EnquiryFlightService } from 'src/app/services/enquiry-flights/enquiry-flights.service';
import { FlightChecklistTemplateService } from 'src/app/services/flight-checklist-templates/flight-checklist-templates.service';
import { FlightChecklistService } from 'src/app/services/flight-checklists/flight-checklists.service';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { AppDataService } from 'src/app/services/data/app-data.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-enquiry-flight-checklist',
    templateUrl: './enquiry-flight-checklist.component.html',
    styleUrls: ['./enquiry-flight-checklist.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
      TooltipDirective,
      FaIconComponent,
      RouterLink,
      FormsModule,
      DatePipe,
      TranslateModule
    ]
})
export class EnquiryFlightChecklistComponent implements OnInit, OnDestroy, OnChanges {
  @Input() enquiryFlight: IEnquiryFlight | null = null;

  faEdit = faEdit;
  faCheck = faCheck;
  faCancel = faCancel;
  faClockRotateLeft = faClockRotateLeft;

  EnumAcl = EnumAcl;
  EnumFlightChecklistTemplateFieldType = EnumFlightChecklistTemplateFieldType;
  EnumFlightChecklistTemplateFieldOptionStatus = EnumFlightChecklistTemplateFieldOptionStatus;

  flightChecklistTemplateByEnquiryType: { [enquiryType: string]: IFlightChecklistTemplate | null } =
    {};
  flightChecklistByEnquiryFlight: { [enquiryFlightId: string]: IFlightChecklist | null } = {};
  flightChecklistTemplate: IFlightChecklistTemplate | null = null;
  flightChecklistTemplateFieldsById: { [fieldId: string]: IFlightChecklistTemplateField } = {};
  loadingFlightChecklistTemplate = false;

  flightChecklist: IFlightChecklist | null = null;
  loadingFlightChecklist = false;

  flightChecklistsForEnquiry: IFlightChecklist[] = [];
  loadingFlightChecklistForEnquiry = false;

  enquiryFlightsForEnquiry: IEnquiryFlight[] = [];
  loadingEnquiryFlightsForEnquiry = false;

  currentUser: IUser | null = null;

  flightChecklistFieldValuesById: {
    [fieldId: string]: {
      field: IFlightChecklistTemplateField;
      value: IFlightChecklistFieldDataValue | null;
      selectedOption: IFlightChecklistTemplateFieldOption | null;
      history: IFlightChecklistFieldDataValue[];
    };
  } = {};

  editingChecklistFieldId: string | null = null;

  showHistoryFieldId: string | null = null;

  private subscriptions = new Subscription();
  private subscriptionFlightChecklist = new Subscription();

  constructor(
    private authService: AuthService,
    private loaderService: LoaderService,
    private flightChecklistTemplateService: FlightChecklistTemplateService,
    private flightChecklistService: FlightChecklistService,
    private enquiryFlightService: EnquiryFlightService,
    private appDataService: AppDataService,
    private aclService: AclService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.authService.userObservable.subscribe((user: IUser) => {
        this.currentUser = user;
      })
    );

    this.loadFlightChecklistAndTemplate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.enquiryFlight) {
      this.loadFlightChecklistAndTemplate();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.subscriptionFlightChecklist.unsubscribe();
  }

  hasAclAccess(id: EnumAcl): boolean {
    return this.aclService.hasAclAccess(id);
  }

  loadFlightChecklistAndTemplate(): void {
    if (this.enquiryFlight) {
      if (this.subscriptionFlightChecklist) {
        this.subscriptionFlightChecklist.unsubscribe();
      }

      this.subscriptionFlightChecklist = new Subscription();

      this.loadFlightChecklistTemplate();
      this.loadFlightChecklist();
      this.loadFlightChecklistsForEnquiry();
      this.loadEnquiryFlightsForEnquiry();
    }
  }

  private loadFlightChecklistTemplate(): void {
    if (this.enquiryFlight) {
      this.loadingFlightChecklistTemplate = true;

      this.subscriptionFlightChecklist.add(
        this.flightChecklistTemplateService
          .getOneForEnquiryType(this.enquiryFlight.enquiryType)
          .subscribe((flightChecklistTemplate: IFlightChecklistTemplate) => {
            this.flightChecklistTemplate = flightChecklistTemplate;

            if (this.flightChecklistTemplate) {
              this.flightChecklistTemplateByEnquiryType[this.flightChecklistTemplate.enquiryType] =
                this.flightChecklistTemplate;

              for (const module of this.flightChecklistTemplate.modules) {
                for (const field of module.fields) {
                  this.flightChecklistTemplateFieldsById[field.id] = field;
                }
              }
            }

            this.refreshFlightChecklist();

            this.loadingFlightChecklistTemplate = false;
          })
      );
    }
  }

  loadFlightChecklist(): void {
    if (this.enquiryFlight) {
      this.loadingFlightChecklist = true;

      this.subscriptionFlightChecklist.add(
        this.flightChecklistService
          .getForFlight(this.enquiryFlight.id)
          .subscribe((flightChecklist: IFlightChecklist) => {
            this.flightChecklist = flightChecklist;

            if (this.flightChecklist) {
              this.flightChecklistByEnquiryFlight[this.enquiryFlight.id] = this.flightChecklist;
            }

            this.refreshFlightChecklist();

            this.loadingFlightChecklist = false;
          })
      );
    }
  }

  loadFlightChecklistsForEnquiry(): void {
    if (this.enquiryFlight?.enquiryId) {
      this.loadingFlightChecklistForEnquiry = true;

      this.subscriptionFlightChecklist.add(
        this.flightChecklistService
          .getForEnquiry(this.enquiryFlight.enquiryId)
          .subscribe((flightChecklists: IFlightChecklist[]) => {
            this.flightChecklistsForEnquiry = flightChecklists;

            this.refreshFlightChecklistForEnquiry();

            this.loadingFlightChecklistForEnquiry = false;
          })
      );
    }
  }

  loadEnquiryFlightsForEnquiry(): void {
    if (this.enquiryFlight?.enquiryId) {
      this.loadingEnquiryFlightsForEnquiry = true;

      this.subscriptionFlightChecklist.add(
        this.enquiryFlightService
          .getAllForEnquiry(this.enquiryFlight.enquiryId)
          .subscribe((enquiryFlights: IEnquiryFlight[]) => {
            this.enquiryFlightsForEnquiry = enquiryFlights;

            this.refreshFlightChecklistForEnquiry();

            this.loadingEnquiryFlightsForEnquiry = false;
          })
      );
    }
  }

  refreshFlightChecklistForEnquiry(): void {
    if (this.flightChecklistTemplate) {
      const flightChecklistByEnquiryFlight: { [enquiryFlightId: string]: IFlightChecklist | null } =
        {};

      for (const flightChecklist of this.flightChecklistsForEnquiry) {
        flightChecklistByEnquiryFlight[flightChecklist.enquiryFlightId] = flightChecklist;
      }

      // Check if checklist exist for flight
      for (const enquiryFlight of this.enquiryFlightsForEnquiry) {
        if (typeof flightChecklistByEnquiryFlight[enquiryFlight.id] === 'undefined') {
          flightChecklistByEnquiryFlight[enquiryFlight.id] = initFlightChecklistFromTemplate(
            enquiryFlight,
            this.flightChecklistTemplate
          );
        }
      }

      this.flightChecklistsForEnquiry = Object.values(flightChecklistByEnquiryFlight);
    }
  }

  refreshFlightChecklist(): void {
    if (this.flightChecklistTemplate && !this.flightChecklist?.id) {
      this.flightChecklist = initFlightChecklistFromTemplate(
        this.enquiryFlight,
        this.flightChecklistTemplate
      );
    }

    this.flightChecklistFieldValuesById = {};

    if (this.flightChecklist) {
      if (this.flightChecklistTemplate) {
        for (const module of this.flightChecklistTemplate.modules) {
          for (const field of module.fields) {
            this.flightChecklistFieldValuesById[field.id] = {
              field,
              value: { value: field.defaultOptionId ?? null },
              selectedOption: null,
              history: []
            };
          }
        }
      }

      for (const fieldData of this.flightChecklist.fieldData) {
        if (this.flightChecklistTemplateFieldsById[fieldData.fieldId]) {
          let selectedOption: IFlightChecklistTemplateFieldOption | null = null;
          let value: string | null = null;

          if (fieldData.history.length) {
            value = fieldData.history[fieldData.history.length - 1].value;
          } else {
            value = fieldData.defaultOptionId;
          }

          if (
            value &&
            this.flightChecklistTemplateFieldsById[fieldData.fieldId].type ===
              EnumFlightChecklistTemplateFieldType.select
          ) {
            for (const option of this.flightChecklistTemplateFieldsById[fieldData.fieldId]
              .options) {
              if (option.id === value) {
                selectedOption = option;
                break;
              }
            }
          }

          if (fieldData.history.length) {
            this.flightChecklistFieldValuesById[fieldData.fieldId] = {
              field: this.flightChecklistTemplateFieldsById[fieldData.fieldId],
              value: { ...fieldData.history[fieldData.history.length - 1] },
              selectedOption,
              history: fieldData.history
            };
          } else {
            this.flightChecklistFieldValuesById[fieldData.fieldId] = {
              field: this.flightChecklistTemplateFieldsById[fieldData.fieldId],
              value: {
                value: fieldData.defaultOptionId ?? null
              },
              selectedOption,
              history: []
            };
          }
        }
      }
    }
  }

  editChecklistField(fieldId: string): void {
    this.editingChecklistFieldId = fieldId;
  }

  clearEditChecklistField(): void {
    this.editingChecklistFieldId = null;

    this.refreshFlightChecklist();
  }

  async saveEditChecklistField(): Promise<void> {
    this.loaderService.presentLoader();

    try {
      const fieldId = this.editingChecklistFieldId;
      const field = fieldId ? this.flightChecklistTemplateFieldsById[fieldId] : null;
      if (!field || !this.flightChecklist || !this.flightChecklistFieldValuesById[fieldId]?.value) {
        throw new Error('Checklist field is no longer available.');
      }

      const value = this.flightChecklistFieldValuesById[fieldId]?.value?.value ?? null;
      const historyValue: IFlightChecklistFieldDataValue = {
        value,
        userId: this.currentUser?.id,
        userInitials: this.currentUser?.initials,
        userFullName: getUserFullname(this.currentUser),
        date: new Date()
      };
      const savedChecklist = appendFlightChecklistFieldValue(this.flightChecklist, field, historyValue);

      savedChecklist.id = savedChecklist.id
        ? await this.flightChecklistService.update(savedChecklist)
        : await this.flightChecklistService.create(savedChecklist);
      this.flightChecklist = savedChecklist;

      // Check if field is part of shared module
      await this.updateFieldValueOnAllOtherFlightChecklist(fieldId, historyValue);

      this.clearEditChecklistField();

      await this.loaderService.hideLoaderOnSuccess();
    } catch (err) {
      await this.loaderService.hideLoaderOnFailure(err.message);
    }
  }

  async updateFieldValueOnAllOtherFlightChecklist(
    fieldId: string,
    historyValue: IFlightChecklistFieldDataValue
  ): Promise<void> {
    if (this.flightChecklistTemplate?.modules?.length) {
      for (const module of this.flightChecklistTemplate.modules) {
        for (const field of module.fields) {
          if (field.id === fieldId) {
            if (module.isSharedAmongAllLegs) {
              // We update field value on all enquiry flights
              for (const flightChecklist of this.flightChecklistsForEnquiry) {
                if (flightChecklist.enquiryFlightId !== this.enquiryFlight.id) {
                  const savedChecklist = appendFlightChecklistFieldValue(
                    flightChecklist,
                    field,
                    historyValue
                  );
                  savedChecklist.id = savedChecklist.id
                    ? await this.flightChecklistService.update(savedChecklist)
                    : await this.flightChecklistService.create(savedChecklist);
                  this.flightChecklistsForEnquiry = this.flightChecklistsForEnquiry.map(item =>
                    item.enquiryFlightId === savedChecklist.enquiryFlightId ? savedChecklist : item
                  );
                }
              }
            }

            break;
          }
        }
      }
    }
  }

  getFieldValueFromOption(fieldId: string, value: string): string | null {
    if (
      this.flightChecklistTemplateFieldsById[fieldId] &&
      this.flightChecklistTemplateFieldsById[fieldId].type ===
        EnumFlightChecklistTemplateFieldType.select
    ) {
      for (const option of this.flightChecklistTemplateFieldsById[fieldId].options) {
        if (option.id === value) {
          return option.text;
        }
      }
    }

    return value;
  }

  getFieldStatusFromOption(
    fieldId: string,
    value: string
  ): EnumFlightChecklistTemplateFieldOptionStatus | null {
    if (
      this.flightChecklistTemplateFieldsById[fieldId] &&
      this.flightChecklistTemplateFieldsById[fieldId].type ===
        EnumFlightChecklistTemplateFieldType.select
    ) {
      for (const option of this.flightChecklistTemplateFieldsById[fieldId].options) {
        if (option.id === value) {
          return option.status;
        }
      }
    }

    return null;
  }

  showFieldHistory(fieldId: string): void {
    if (this.showHistoryFieldId === fieldId) {
      this.showHistoryFieldId = null;
    } else {
      this.showHistoryFieldId = fieldId;
    }
  }
}
